import { NgClass } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  inject,
  Input,
  Output,
  ViewEncapsulation,
} from '@angular/core';
import { NzTableModule, NzTableQueryParams } from 'ng-zorro-antd/table';
import { UITableBodyColumn, UITableHeaderColumn } from '../components';
import {
  ITableActionsColumnReturnData,
  ITableColumnConfigType,
  ITableConfig,
  TableActionsColumnType,
} from '../models';
import { UILoading } from '../../loading';
import { GetFieldValuePipe } from '@shared/core';
import { LocalizationPipe } from '@shared/modules/localization';
@Component({
  selector: 'ui-table',
  templateUrl: './table.component.html',
  styleUrl: './table.component.scss',
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    NgClass,
    NzTableModule,
    UITableHeaderColumn,
    UITableBodyColumn,
    UILoading,
    GetFieldValuePipe,
    LocalizationPipe,
  ],
  providers: [GetFieldValuePipe],
})
export class UITable {
  @Input() loading = true;
  @Input() config?: ITableConfig;
  @Input() data: any[] = [];

  @Input() pageSizeOptions: number[] = [10, 20, 30, 40, 50, 100];
  @Input() pageSize = 20;
  @Input() pageIndex = 1;
  @Input() total = 0;

  @Input() set selectedRows(rows: any[]) {
    this.setOfCheckedId = new Set(rows ?? []);
  }
  @Input() rowSelectionDisabled = false;

  @Input() scrollHeight = '100%';

  @Output() pageIndexChange = new EventEmitter<number>();
  @Output() pageSizeChange = new EventEmitter<number>();

  @Output() updateTableData = new EventEmitter<void>();
  @Output() rowSelected = new EventEmitter<any[]>();

  @Output() actionClicked = new EventEmitter<ITableActionsColumnReturnData>();

  get tableData() {
    return this.isFrontPagination
      ? this.data.slice(this.pageIndex - 1, this.pageIndex * this.pageSize)
      : this.data;
  }

  get isFrontPagination(): boolean {
    return this.config?.paginationType === 'client-side';
  }

  get showPagination(): boolean {
    return this.config?.paginationType !== 'none' && this.total > 10;
  }

  get isRowSelectionTable(): boolean {
    return this.config?.rowSelection?.type != null && this.config.rowSelection.type != 'none';
  }

  trackBy = (rowData: unknown, index?: number) => {
    if (this.config?.trackBy) {
      return this.config?.trackBy(rowData);
    }
    return index;
  };

  get rowSelectionByField(): string | undefined {
    return this.config?.rowSelection?.selectByField;
  }
  checked = false;
  indeterminate = false;

  setOfCheckedId = new Set<any>();

  private getFieldValuePipe = inject(GetFieldValuePipe);

  updateCheckedSet(id: any, checked: boolean): void {
    if (checked) {
      this.setOfCheckedId.add(id);
    } else {
      this.setOfCheckedId.delete(id);
    }
    this.rowSelected.emit(Array.from(this.setOfCheckedId));
  }

  onItemChecked(row: any, checked: boolean): void {
    this.updateCheckedSet(this.getFieldValuePipe.transform(row, this.rowSelectionByField), checked);
    this.refreshCheckedStatus();
  }

  refreshCheckedStatus(): void {
    this.checked = this.data?.every(row =>
      this.setOfCheckedId.has(this.getFieldValuePipe.transform(row, this.rowSelectionByField)),
    );
    this.indeterminate =
      this.data?.some(row =>
        this.setOfCheckedId.has(this.getFieldValuePipe.transform(row, this.rowSelectionByField)),
      ) && !this.checked;
  }

  onAllChecked(checked: boolean): void {
    this.data?.forEach(row =>
      this.updateCheckedSet(
        this.getFieldValuePipe.transform(row, this.rowSelectionByField),
        checked,
      ),
    );
    this.refreshCheckedStatus();
  }

  get tableColumns(): ITableColumnConfigType[] {
    return (this.config?.columns ?? []).filter(column => !column.isHidden);
  }

  onQueryParamsChange(params: NzTableQueryParams): void {
    const { pageSize, pageIndex, sort, filter } = params;
    if (pageSize !== this.pageSize) {
      this.onPageSizeChange(pageSize);
    }
    if (pageIndex !== this.pageIndex) {
      this.onPageIndexChange(pageIndex);
    }
    this.onUpdateTableData();

    // const currentSort = sort.find((item) => item.value !== null);
    // const sortField = (currentSort && currentSort.key) || null;
    // const sortOrder = (currentSort && currentSort.value) || null;
    // this.loadDataFromServer(pageIndex, pageSize, sortField, sortOrder, filter);
  }

  private onPageIndexChange(pageIndex: number): void {
    this.pageIndex = pageIndex;
    this.pageIndexChange.emit(pageIndex);
  }

  private onPageSizeChange(pageSize: number): void {
    this.pageIndex = pageSize;
    this.pageSizeChange.emit(pageSize);
  }

  onUpdateTableData(): void {
    this.updateTableData.emit();
  }

  onRowClicked(action: TableActionsColumnType, rowData: any): void {
    this.actionClicked.emit({
      type: action,
      rowData: rowData,
    });
  }
}
